import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Icon } from 'semantic-ui-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { UserContext } from '../../context/User';
import {
  API,
  copy,
  getSystemName,
  isAdmin,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import './Dashboard.css';

const chartColors = {
  requests: '#2684ff',
  quota: '#13b887',
  tokens: '#7c5cff',
  bar: ['#2684ff', '#13b887', '#ffb020', '#e55986', '#7c5cff', '#00a6a6'],
};

const adminModules = [
  {
    to: '/model',
    icon: 'cubes',
    label: '模型管理',
    metricKey: 'managed_model_count',
    fallback: '0',
    detail: '模型目录、默认参数、三方 API',
    tone: 'violet',
  },
  {
    to: '/channel',
    icon: 'sitemap',
    label: '渠道管理',
    metricKey: 'channel_count',
    fallback: '0',
    detail: '上游通道、权重、健康状态',
    tone: 'green',
  },
  {
    to: '/operator',
    icon: 'server',
    label: '运营管理',
    metricKey: 'enabled_channel_count',
    fallback: '0',
    detail: '套餐、客户资产、运营分析',
    tone: 'blue',
  },
  {
    to: '/user',
    icon: 'users',
    label: '用户管理',
    metricKey: null,
    fallback: '管理',
    detail: '账号、角色、余额与状态',
    tone: 'orange',
  },
  {
    to: '/log',
    icon: 'book',
    label: '调用日志',
    metricKey: 'token_count',
    fallback: '0',
    detail: '请求记录、模型消耗、异常追踪',
    tone: 'pink',
  },
];

function getLocalDateString(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildSevenDaySeries(records) {
  const daily = {};
  const start = new Date();
  start.setDate(start.getDate() - 6);

  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const key = getLocalDateString(date);
    daily[key] = {
      date: key,
      requests: 0,
      quota: 0,
      tokens: 0,
    };
  }

  records.forEach((item) => {
    if (!daily[item.Day]) {
      return;
    }
    daily[item.Day].requests += item.RequestCount || 0;
    daily[item.Day].quota += recordAmountRMB(item);
    daily[item.Day].tokens += (item.PromptTokens || 0) + (item.CompletionTokens || 0);
  });

  return Object.values(daily);
}

function buildModelSeries(records) {
  const base = buildSevenDaySeries([]).reduce((acc, item) => {
    acc[item.date] = { date: item.date };
    return acc;
  }, {});

  records.forEach((item) => {
    const modelName = item.ModelName || 'unknown';
    if (!base[item.Day]) {
      return;
    }
    base[item.Day][modelName] =
      (base[item.Day][modelName] || 0) +
      (item.PromptTokens || 0) +
      (item.CompletionTokens || 0);
  });

  return Object.values(base);
}

function formatDate(dateStr) {
  const date = new Date(`${dateStr}T00:00:00`);
  return date.toLocaleDateString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
  });
}

function formatCompactNumber(value) {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return `${value || 0}`;
}

function formatBalance(quota) {
  const quotaPerUnit = parseFloat(localStorage.getItem('quota_per_unit') || '1');
  const amount = quotaPerUnit > 0 ? (quota || 0) / quotaPerUnit : quota || 0;
  const fractionDigits = amount === 0 || Math.abs(amount) >= 1 ? 2 : 8;
  return `¥${amount.toLocaleString('zh-CN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

function formatCurrencyAmount(amount) {
  const value = Number(amount || 0);
  const fractionDigits = value === 0 || Math.abs(value) >= 1 ? 2 : 8;
  return `¥${value.toLocaleString('zh-CN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

function recordAmountRMB(record) {
  if (record && record.AmountRMB !== undefined && record.AmountRMB !== null) {
    return Number(record.AmountRMB || 0);
  }
  const quotaPerUnit = parseFloat(localStorage.getItem('quota_per_unit') || '1');
  return quotaPerUnit > 0 ? Number(record?.Quota || 0) / quotaPerUnit : Number(record?.Quota || 0);
}

function maskTokenKey(key) {
  if (!key) {
    return '暂无可用令牌';
  }
  if (key.length <= 16) {
    return key;
  }
  return `${key.slice(0, 8)}...${key.slice(-5)}`;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 6) return '夜深了';
  if (hour < 12) return '上午好';
  if (hour < 18) return '下午好';
  return '晚上好';
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [userState, userDispatch] = useContext(UserContext);
  const [user, setUser] = useState(userState.user || {});
  const [records, setRecords] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [adminMetrics, setAdminMetrics] = useState(null);
  const [, setLoading] = useState(true);
  const adminUser = isAdmin();

  const systemName = getSystemName();
  const activeToken = tokens.find((token) => token.status === 1) || tokens[0];
  const tokenValue = activeToken ? `sk-${activeToken.key}` : '';
  const displayName =
    user.display_name || user.username || userState.user?.username || '用户';

  const refreshDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const dashboardRequests = [
        API.get('/api/user/dashboard'),
        API.get('/api/user/self'),
        API.get('/api/token/?p=0&order='),
      ];

      if (adminUser) {
        dashboardRequests.push(API.get('/api/metrics'));
      }

      const [dashboardRes, userRes, tokenRes, metricsRes] = await Promise.all(
        dashboardRequests
      );

      if (dashboardRes?.data?.success) {
        setRecords(dashboardRes.data.data || []);
      } else if (dashboardRes?.data?.message) {
        showError(dashboardRes.data.message);
      }

      if (userRes?.data?.success) {
        const freshUser = userRes.data.data || {};
        setUser(freshUser);
        userDispatch({ type: 'login', payload: freshUser });
        localStorage.setItem('user', JSON.stringify(freshUser));
      }

      if (tokenRes?.data?.success) {
        setTokens(tokenRes.data.data || []);
      }

      if (adminUser && metricsRes?.data?.success) {
        setAdminMetrics(metricsRes.data.data || {});
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [adminUser, userDispatch]);

  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  const summary = useMemo(() => {
    const today = getLocalDateString(new Date());
    const todayItems = records.filter((item) => item.Day === today);
    return {
      requests: todayItems.reduce((sum, item) => sum + (item.RequestCount || 0), 0),
      quota: todayItems.reduce((sum, item) => sum + (item.Quota || 0), 0),
      amountRMB: todayItems.reduce((sum, item) => sum + recordAmountRMB(item), 0),
      tokens: todayItems.reduce(
        (sum, item) => sum + (item.PromptTokens || 0) + (item.CompletionTokens || 0),
        0
      ),
    };
  }, [records]);

  const timeSeriesData = useMemo(() => buildSevenDaySeries(records), [records]);
  const modelData = useMemo(() => buildModelSeries(records), [records]);
  const modelNames = useMemo(() => {
    return [...new Set(records.map((item) => item.ModelName).filter(Boolean))];
  }, [records]);

  const adminModuleCards = useMemo(() => {
    return adminModules.map((item) => {
      let value = item.fallback;
      if (item.metricKey && adminMetrics?.[item.metricKey] !== undefined) {
        value = formatCompactNumber(Number(adminMetrics[item.metricKey] || 0));
      }
      if (item.to === '/channel' && adminMetrics) {
        value = `${formatCompactNumber(
          Number(adminMetrics.enabled_channel_count || 0)
        )}/${formatCompactNumber(Number(adminMetrics.channel_count || 0))}`;
      }
      return { ...item, value };
    });
  }, [adminMetrics]);

  const handleCopyToken = async () => {
    if (!tokenValue) {
      navigate('/token/add');
      return;
    }
    if (await copy(tokenValue)) {
      showSuccess('API 令牌已复制');
    } else {
      showError('复制失败，请手动复制');
    }
  };

  const xAxisConfig = {
    dataKey: 'date',
    axisLine: false,
    tickLine: false,
    tick: { fontSize: 12, fill: '#8a96a8' },
    tickFormatter: formatDate,
  };

  return (
    <div className='sino-dashboard'>
      <section className='sino-dashboard-hero'>
        <div>
          <h2>
            <Icon name='hand paper outline' />
            {getGreeting()}，{displayName}
          </h2>
          <p>{adminUser ? `${systemName} 后台管理总览` : `${systemName} API 网关账户概览`}</p>
        </div>
      </section>

      {adminUser && (
        <section className='sino-admin-section'>
          <div className='sino-section-head'>
            <div>
              <h3>后台工作台</h3>
              <p>统一管理模型、渠道、运营数据与用户资产。</p>
            </div>
            <Button as={Link} to='/operator' basic primary>
              运营看板
            </Button>
          </div>
          <div className='sino-admin-grid'>
            {adminModuleCards.map((item) => (
              <Link
                className={`sino-admin-card tone-${item.tone}`}
                to={item.to}
                key={item.to}
              >
                <span className='sino-admin-icon'>
                  <Icon name={item.icon} />
                </span>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <small>{item.detail}</small>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className='sino-primary-grid'>
        <article className='sino-card sino-account-card'>
          <div className='sino-card-title'>
            <Icon name='calendar outline' />
            <span>账户数据</span>
          </div>
          <div className='sino-balance-row'>
            <div className='sino-currency-badge'>
              <Icon name='yen sign' />
            </div>
            <div>
              <span>当前余额</span>
              <strong>{formatBalance(user.quota)}</strong>
            </div>
            <Button as={Link} to='/topup' size='small' basic primary>
              充值
            </Button>
          </div>
        </article>

        <article className='sino-card sino-token-card'>
          <div className='sino-card-title'>
            <Icon name='flask' />
            <span>API 令牌（按量计费）</span>
          </div>
          <div className='sino-token-row'>
            <code>{maskTokenKey(tokenValue)}</code>
            <Button primary onClick={handleCopyToken}>
              <Icon name={tokenValue ? 'copy' : 'plus'} />
              {tokenValue ? '复制' : '创建'}
            </Button>
            <Button as={Link} to='/token' basic>
              管理
            </Button>
          </div>
          {activeToken ? (
            <p className='sino-token-meta'>
              {activeToken.name || '默认令牌'} ·
              {activeToken.unlimited_quota
                ? ' 不限余额'
                : ` 剩余余额 ${formatBalance(activeToken.remain_quota)}`}
              {activeToken.accessed_time > 0
                ? ` · 最近访问 ${timestamp2string(activeToken.accessed_time)}`
                : ''}
            </p>
          ) : (
            <p className='sino-token-meta'>还没有 API 令牌，可立即创建一个。</p>
          )}
        </article>
      </section>

      <section className='sino-stat-grid'>
        <article className='sino-stat-card'>
          <span>今日请求</span>
          <strong>{formatCompactNumber(summary.requests)}</strong>
          <small>Requests</small>
        </article>
        <article className='sino-stat-card accent-green'>
          <span>今日消费</span>
          <strong>{formatCurrencyAmount(summary.amountRMB)}</strong>
          <small>实际计费</small>
        </article>
        <article className='sino-stat-card accent-violet'>
          <span>今日 Token</span>
          <strong>{formatCompactNumber(summary.tokens)}</strong>
          <small>Prompt + Completion</small>
        </article>
      </section>

      <section className='sino-chart-grid'>
        <article className='sino-panel'>
          <div className='sino-panel-head'>
            <h3>7 天调用趋势</h3>
            <span>请求 / 消费 / Token</span>
          </div>
          <div className='sino-chart-box'>
            <ResponsiveContainer width='100%' height={230}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e8eef6' />
                <XAxis {...xAxisConfig} />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    border: '1px solid #e6edf5',
                    borderRadius: 8,
                    boxShadow: '0 12px 28px rgba(24, 35, 54, 0.08)',
                  }}
                  labelFormatter={(label) => `日期：${formatDate(label)}`}
                />
                <Line
                  type='monotone'
                  dataKey='requests'
                  stroke={chartColors.requests}
                  strokeWidth={2}
                  dot={false}
                  name='请求'
                />
                <Line
                  type='monotone'
                  dataKey='quota'
                  stroke={chartColors.quota}
                  strokeWidth={2}
                  dot={false}
                  name='消费'
                />
                <Line
                  type='monotone'
                  dataKey='tokens'
                  stroke={chartColors.tokens}
                  strokeWidth={2}
                  dot={false}
                  name='Token'
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className='sino-panel'>
          <div className='sino-panel-head'>
            <h3>模型调用分析</h3>
            <span>{modelNames.length ? `${modelNames.length} 个模型` : '暂无调用'}</span>
          </div>
          <div className='sino-chart-box'>
            {modelNames.length > 0 ? (
              <ResponsiveContainer width='100%' height={230}>
                <BarChart data={modelData}>
                  <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e8eef6' />
                  <XAxis {...xAxisConfig} />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{
                      border: '1px solid #e6edf5',
                      borderRadius: 8,
                      boxShadow: '0 12px 28px rgba(24, 35, 54, 0.08)',
                    }}
                    labelFormatter={(label) => `日期：${formatDate(label)}`}
                  />
                  {modelNames.map((model, index) => (
                    <Bar
                      key={model}
                      dataKey={model}
                      stackId='models'
                      fill={chartColors.bar[index % chartColors.bar.length]}
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className='sino-empty-chart'>暂无模型调用数据</div>
            )}
          </div>
        </article>
      </section>
    </div>
  );
};

export default Dashboard;
